import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";
import { API_BASE_URL } from "../../../../../environment";
import company from "../../../../../assets/company.png";

interface Subscription {
  tenant_subscription_id: number;
  subscription_display_name: string;
  max_user: number | null;
  amount: number | null;
  interval_length: string;
  is_trial: boolean;
  interval_display_name: string;
  interval_key: string;
  interval_id: string;
  tenant_id: string;
  tenant_name: string;
  domain_name: string;
  time_zone: string | null;
  language: string | null;
  currency_code: string | null;
  created_date: string;
  updated_date: string;
  subscription_start_date: string;
  subscription_end_date: string;
  is_active: boolean;
  is_expired: boolean;
  created_datetime: string;
  updated_datetime: string;
  number_of_user: number;
  subscription_id: number;
}

interface AnalyticsData {
  recently_added: Subscription[];
  recently_expired: Subscription[];
  monthly_near_expiry: Subscription[];
  yearly_near_expiry: Subscription[];
}

const SubscriptionStatsCards = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const authHeader = localStorage.getItem("authCred") || "";
        const response = await fetch(
          `${API_BASE_URL}/dashboard/tenant-subscription-analytics/`,
          {
            headers: {
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Error fetching subscription analytics:", error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getSubscriptionIcon = (isActive: boolean, isTrial: boolean) => {
    if (!isActive) return "company-icon-16.svg";
    if (isTrial) return "company-icon-01.svg";
    return "company-icon-02.svg";
  };

  const getSubscriptionBadge = (isActive: boolean, isTrial: boolean) => {
    if (!isActive) return "text-danger";
    if (isTrial) return "text-warning";
    return "text-success";
  };

  const nearExpirySubscriptions = [
    ...(data?.monthly_near_expiry || []),
    ...(data?.yearly_near_expiry || []),
  ].slice(0, 5);

  if (loading) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  Loading subscription data...
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-xxl-4 col-xl-12 d-flex">
        <div className="card flex-fill">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="mb-0 fs-16 fw-bold d-flex align-items-center">
              <span className="db-chart-icon">
                <i className="ti ti-package" />
              </span>
              Recently Added Subscriptions
            </h5>
          </div>
          <div className="card-body pb-2">
            {data?.recently_added && data.recently_added.length > 0 ? (
              data.recently_added.slice(0, 5).map((subscription, index) => (
                <div
                  key={subscription.tenant_subscription_id}
                  className={`d-sm-flex justify-content-between flex-wrap ${
                    index < 4 ? "mb-4" : "mb-0"
                  }`}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-md border rounded-circle flex-shrink-0">
                      <img
                        src={company}
                        className="img-fluid w-auto h-auto"
                        alt="img"
                      />
                    </div>
                    <div className="ms-2 flex-fill">
                      <h6 className="fw-medium text-truncate mb-1 fs-14">
                        <span className="text-dark">
                          {subscription.tenant_name}
                        </span>
                      </h6>
                      <p className="fs-13 mb-0">
                        {formatDate(subscription.created_date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm-end mb-0">
                    <h6
                      className={`fw-medium text-truncate mb-1 fs-14 ${getSubscriptionBadge(
                        subscription.is_active,
                        subscription.is_trial
                      )}`}
                    >
                      {subscription.number_of_user} Users
                    </h6>
                    <p className="fs-13 mb-0">
                      {subscription.subscription_display_name} (
                      {subscription.interval_display_name}ly)
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <i
                  className="ti ti-package-off text-muted mb-3"
                  style={{ fontSize: "2rem" }}
                ></i>
                <p className="text-muted">No recently added subscriptions</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* /Recent Transactions */}
      <div className="col-xxl-4 col-xl-12 d-flex">
        <div className="card flex-fill">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="mb-0 fs-16 fw-bold d-flex align-items-center">
              <span className="db-chart-icon">
                <i className="ti ti-clock-exclamation" />
              </span>
              Near to Expire Subscriptions
            </h5>
          </div>
          <div className="card-body pb-2">
            {nearExpirySubscriptions && nearExpirySubscriptions.length > 0 ? (
              nearExpirySubscriptions.map((subscription, index) => (
                <div
                  key={subscription.tenant_subscription_id}
                  className={`d-sm-flex justify-content-between flex-wrap ${
                    index < 4 ? "mb-4" : "mb-0"
                  }`}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-md border rounded-circle flex-shrink-0">
                      <img
                        src={company}
                        className="img-fluid w-auto h-auto"
                        alt="img"
                      />
                    </div>
                    <div className="ms-2 flex-fill">
                      <h6 className="fw-medium text-truncate mb-1 fs-14">
                        <span className="text-dark">
                          {subscription.tenant_name}
                        </span>
                      </h6>
                      <p className="fs-13 mb-0">
                        {subscription.subscription_display_name} (
                        {subscription.interval_display_name}ly)
                      </p>
                    </div>
                  </div>
                  <div className="text-sm-end mb-0">
                    <p className="fs-14 mb-0 text-warning">
                      Expires: {formatDate(subscription.subscription_end_date)}
                    </p>
                    <h6 className="fw-normal text-truncate mb-0 fs-14">
                      {subscription.number_of_user} Users
                    </h6>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <i
                  className="ti ti-clock-off text-muted mb-3"
                  style={{ fontSize: "2rem" }}
                ></i>
                <p className="text-muted">No subscriptions near expiry</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="col-xxl-4 col-xl-12 d-flex">
        <div className="card flex-fill">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="mb-0 fs-16 fw-bold d-flex align-items-center">
              <span className="db-chart-icon">
                <i className="ti ti-calendar-x" />
              </span>
              Recently Expired Subscriptions
            </h5>
          </div>
          <div className="card-body pb-2">
            {data?.recently_expired && data.recently_expired.length > 0 ? (
              data.recently_expired.slice(0, 5).map((subscription, index) => (
                <div
                  key={subscription.tenant_subscription_id}
                  className={`d-sm-flex justify-content-between flex-wrap ${
                    index < 4 ? "mb-4" : "mb-0"
                  }`}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-md border rounded-circle flex-shrink-0">
                      <img
                        src={company}
                        className="img-fluid w-auto h-auto"
                        alt="img"
                      />
                    </div>
                    <div className="ms-2 flex-fill">
                      <h6 className="fw-medium text-truncate mb-1 fs-14">
                        <span className="text-dark">
                          {subscription.tenant_name}
                        </span>
                      </h6>
                      <p className="fs-13 mb-0">
                        {formatDate(subscription.subscription_end_date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm-end mb-0">
                    <h6 className="fw-medium text-truncate mb-1 fs-14 text-danger">
                      Expired
                    </h6>
                    <p className="fs-13 mb-0">
                      {subscription.subscription_display_name} (
                      {subscription.interval_display_name}ly)
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <i
                  className="ti ti-calendar-off text-muted mb-3"
                  style={{ fontSize: "2rem" }}
                ></i>
                <p className="text-muted">No recently expired subscriptions</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatsCards;
